import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import * as Mui from "@mui/material";
import ApartmentOutlined from "@mui/icons-material/ApartmentOutlined";
import BusinessOutlined from "@mui/icons-material/BusinessOutlined";
import CheckRounded from "@mui/icons-material/CheckRounded";
import {
  designColors,
  designFontFamily,
  designTheme,
} from "services/constants/designTokens";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { customGetAPIQuiet } from "services/utils/CustomAPI";
import { defaultAllSelection } from "services/constants";
import {
  isOrganisationResponse,
  organisationUrl,
} from "pages/SupportAdmin/Organisation/organisationApi";
import { requestSetupCreate, SETUP_PROGRESS_EVENT } from "./progress";

// First-time organisation setup, as one global prompt dialog at a time on
// any screen: Organisation Details -> Company, both required. The dialog
// holds no form: OK opens the existing screen for that step and steps aside
// while the user is there. When that screen saves, the next step's dialog
// opens. Leaving the screen without saving brings the dialog back.
// The flow is for a new organisation only: it runs while no company exists
// yet, and then shows just the steps that are not done. An organisation
// that already has a company is treated as set up and never sees the
// dialog.
const STEPS = [
  {
    key: "details",
    Icon: ApartmentOutlined,
    title: "Set up your organisation",
    opensCreate: false,
    label: "Organisation Details",
    // Entered under System Configuration; Administration's copy is
    // read-only.
    path: "/admin/support/organisation",
    product: "System Configuration",
    message:
      "Start by entering your organisation details. This is required before you can continue.",
  },
  {
    key: "company",
    Icon: BusinessOutlined,
    title: "Create your first company",
    // OK also opens the screen's create form.
    opensCreate: true,
    label: "Company",
    path: "/admin/administration/companies",
    product: "Administration",
    message:
      "Create your first company. At least one company is required to use the application.",
  },
];

// Whether one step is already done on the server. A status that cannot be
// read counts as done, so a failed request never locks the user behind a
// dialog.
const isStepDone = async (key, domain) => {
  const urls = {
    details: organisationUrl(domain),
    company: `${domain}get-active-company/`,
  };
  let result = null;
  try {
    result = await customGetAPIQuiet(urls[key]);
  } catch {
    return true;
  }
  // A new tenant has no organisation details record yet; the backend
  // answers that with 404 (or an empty body), which must not count as done.
  if (!result?.ok) return !(key === "details" && Number(result?.code) === 404);
  const data = result.responseDetails;
  if (key === "details") {
    const hasRecord =
      isOrganisationResponse(data) && data.organisation_name.trim() !== "";
    return hasRecord && data?.is_default !== true;
  }
  // The backend always returns its default company (id 99999), even for a
  // brand-new tenant, so the company list is never empty. Only real
  // companies count: a list holding just the default one means no company
  // has been created yet.
  if (!Array.isArray(data)) return true;
  return data.some(
    (company) =>
      Number(company?.id ?? company?.company_id) !== defaultAllSelection,
  );
};

export const SetupFlow = () => {
  const history = useHistory();
  const location = useLocation();
  const { domain, userDetails, productAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  const userId = userDetails?.id;
  // The steps live in System Configuration and Administration; only someone
  // who can open both can complete them, so nobody is sent to a screen they
  // cannot use.
  const canCompleteSetup = STEPS.every(
    (step) =>
      Array.isArray(productAccess) &&
      productAccess.some((product) => product?.product_name === step.product),
  );

  // null until the first status check; then { [stepKey]: true when done }.
  const [done, setDone] = useState(null);
  // The step whose screen the user was sent to with Next.
  const [working, setWorking] = useState(null);
  // True once the user has actually reached that screen.
  const arrived = useRef(false);

  const current = done ? STEPS.find((step) => !done[step.key]) : null;
  const stepKey = current?.key;
  const stepPath = current?.path;

  const finish = useCallback((key) => {
    arrived.current = false;
    setWorking(null);
    setDone((previous) => (previous ? { ...previous, [key]: true } : previous));
  }, []);

  // Status check after login. The company check goes first, alone: an
  // organisation that already has a company is set up, so the organisation
  // details request is only sent for a new organisation.
  useEffect(() => {
    if (!domain || !userId || !canCompleteSetup) return undefined;
    let active = true;
    (async () => {
      const alreadySetUp = await isStepDone("company", domain);
      if (!active) return;
      if (alreadySetUp) {
        setDone({ details: true, company: true });
        return;
      }
      const detailsDone = await isStepDone("details", domain);
      if (!active) return;
      setDone({ details: detailsDone, company: false });
    })();
    return () => {
      active = false;
    };
  }, [domain, userId, canCompleteSetup]);

  // The step's own screen reports a successful save (see ./progress).
  useEffect(() => {
    const onProgress = (event) => {
      if (event.detail === stepKey) finish(stepKey);
    };
    window.addEventListener(SETUP_PROGRESS_EVENT, onProgress);
    return () => window.removeEventListener(SETUP_PROGRESS_EVENT, onProgress);
  }, [finish, stepKey]);

  // The user left the step's screen: move on if the step got done there,
  // otherwise show its dialog again.
  useEffect(() => {
    if (!working || working !== stepKey) return undefined;
    if (location.pathname === stepPath) {
      arrived.current = true;
      return undefined;
    }
    if (!arrived.current) return undefined;
    arrived.current = false;
    let active = true;
    isStepDone(stepKey, domain).then((isDone) => {
      if (!active) return;
      if (isDone) finish(stepKey);
      else setWorking(null);
    });
    return () => {
      active = false;
    };
  }, [domain, finish, location.pathname, stepKey, stepPath, working]);

  // Hidden while the user is on the step's screen doing the work.
  if (!current || working === stepKey) return null;

  const next = () => {
    arrived.current = location.pathname === stepPath;
    setWorking(stepKey);
    if (current.opensCreate) requestSetupCreate(stepKey);
    if (location.pathname !== stepPath) history.push(stepPath);
  };

  const stepIndex = STEPS.findIndex((step) => step.key === stepKey);
  const StepIcon = current.Icon;
  const doneCount = STEPS.filter((step) => done[step.key]).length;

  // One row of the checklist: a numbered badge (a tick once done), the step
  // name and its state.
  const stepRow = (step, index) => {
    const isDone = Boolean(done[step.key]);
    const isCurrent = step.key === stepKey;
    const strong = isDone || isCurrent;
    const state = isDone ? "Done" : isCurrent ? "Now" : "Required";
    return (
      <Mui.Stack
        key={step.key}
        direction="row"
        alignItems="center"
        spacing={1.5}
        aria-current={isCurrent ? "step" : undefined}
        sx={{
          px: 1.5,
          py: 1,
          borderRadius: "8px",
          border: "1px solid",
          borderColor: isCurrent ? designColors.teal : "transparent",
          bgcolor: isCurrent ? designColors.tealTint : "transparent",
        }}
      >
        <Mui.Box
          sx={{
            width: 26,
            height: 26,
            flexShrink: 0,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            fontSize: 12.5,
            fontWeight: 700,
            color: strong ? designColors.white : designColors.slate500,
            bgcolor: strong ? designColors.tealDark : designColors.slate200,
          }}
        >
          {isDone ? <CheckRounded sx={{ fontSize: 16 }} /> : index + 1}
        </Mui.Box>
        <Mui.Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 14,
            fontWeight: isCurrent ? 700 : 500,
            color: strong ? designColors.slate900 : designColors.slate500,
          }}
        >
          {step.label}
        </Mui.Typography>
        <Mui.Typography
          sx={{
            fontSize: 12.5,
            fontWeight: 600,
            color: isCurrent ? designColors.tealDark : designColors.slate500,
          }}
        >
          {state}
        </Mui.Typography>
      </Mui.Stack>
    );
  };

  return (
    <Mui.ThemeProvider theme={designTheme}>
      {/* No onClose: the dialog cannot be dismissed, only OK or Skip. */}
      <Mui.Dialog
        open
        fullWidth
        maxWidth="md"
        aria-labelledby="setup-flow-title"
        aria-describedby="setup-flow-message"
        PaperProps={{
          sx: {
            borderRadius: "14px",
            overflow: "hidden",
            fontFamily: designFontFamily,
          },
        }}
      >
        {/* Header band: step icon, where the user is, what to do. */}
        <Mui.Box
          sx={{
            px: { xs: 2.5, sm: 3.5 },
            pt: 3,
            pb: 2.5,
            bgcolor: designColors.tealTint,
            borderBottom: "1px solid " + designColors.slate200,
          }}
        >
          <Mui.Stack direction="row" spacing={2} alignItems="flex-start">
            <Mui.Box
              sx={{
                width: 48,
                height: 48,
                flexShrink: 0,
                borderRadius: "12px",
                display: "grid",
                placeItems: "center",
                color: designColors.white,
                bgcolor: designColors.tealDark,
              }}
            >
              <StepIcon />
            </Mui.Box>
            <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
              <Mui.Stack direction="row" alignItems="center" spacing={1}>
                <Mui.Typography
                  sx={{
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: designColors.tealDark,
                  }}
                >
                  Organisation setup, step {stepIndex + 1} of {STEPS.length}
                </Mui.Typography>
              </Mui.Stack>
              <Mui.Typography
                id="setup-flow-title"
                component="h2"
                sx={{
                  mt: 0.25,
                  fontSize: 20,
                  fontWeight: 700,
                  lineHeight: 1.3,
                  color: designColors.slate900,
                }}
              >
                {current.title}
              </Mui.Typography>
              <Mui.Typography
                id="setup-flow-message"
                sx={{ mt: 0.75, fontSize: 14, color: designColors.slate700 }}
              >
                {current.message}
              </Mui.Typography>
            </Mui.Box>
          </Mui.Stack>
          <Mui.LinearProgress
            variant="determinate"
            value={(doneCount / STEPS.length) * 100}
            aria-label="Setup progress"
            sx={{
              mt: 2.5,
              height: 6,
              borderRadius: "999px",
              bgcolor: designColors.white,
              "& .MuiLinearProgress-bar": {
                borderRadius: "999px",
                bgcolor: designColors.tealDark,
              },
            }}
          />
        </Mui.Box>

        {/* Checklist of every step. */}
        <Mui.Stack spacing={0.5} sx={{ px: { xs: 1.5, sm: 2.5 }, py: 2 }}>
          {STEPS.map(stepRow)}
        </Mui.Stack>

        <Mui.Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={{
            px: { xs: 2.5, sm: 3.5 },
            py: 2,
            borderTop: "1px solid " + designColors.slate200,
            bgcolor: designColors.slateBg,
          }}
        >
          <Mui.Box sx={{ flex: 1 }} />
          <PrimaryButton onClick={next} sx={{ minWidth: 96 }}>
            OK
          </PrimaryButton>
        </Mui.Stack>
      </Mui.Dialog>
    </Mui.ThemeProvider>
  );
};

export default SetupFlow;
